/**
 * Étiquette + aide + message d’erreur autour d’un champ, avec le câblage
 * d’accessibilité fait une fois pour toutes (`id`, `aria-describedby`,
 * `aria-invalid`).
 *
 * ```tsx
 * <FormField label="Motif" requis erreur={erreurs.motif}>
 *   {(champ) => <Textarea {...champ} value={motif} onChange={…} />}
 * </FormField>
 * ```
 */

import type { ReactNode } from 'react'
import { useId } from 'react'

import { cn } from '../lib/cn'

/** Attributs à étaler sur le champ contrôlé. */
export interface AttributsChamp {
  readonly id: string
  readonly 'aria-describedby': string | undefined
  readonly 'aria-invalid': true | undefined
  readonly required: true | undefined
}

export interface FormFieldProps {
  readonly label: ReactNode
  /** Ajoute l’astérisque et `required` (motif obligatoire — ARCHITECTURE §9.4). */
  readonly requis?: boolean | undefined
  /** Explication permanente sous le champ. */
  readonly aide?: ReactNode | undefined
  /** Message d’erreur : remplace l’aide et passe le champ en état invalide. */
  readonly erreur?: string | null | undefined
  /** Force l’identifiant (utile pour un `htmlFor` externe). */
  readonly id?: string | undefined
  readonly className?: string | undefined
  readonly children: ReactNode | ((champ: AttributsChamp) => ReactNode)
}

export function FormField({
  label,
  requis = false,
  aide,
  erreur,
  id,
  className,
  children,
}: FormFieldProps) {
  const idAuto = useId()
  const idChamp = id ?? idAuto
  const idAide = `${idChamp}-aide`
  const idErreur = `${idChamp}-erreur`
  const enErreur = erreur !== null && erreur !== undefined && erreur !== ''

  const decrit = enErreur ? idErreur : aide !== undefined ? idAide : undefined

  const champ: AttributsChamp = {
    id: idChamp,
    'aria-describedby': decrit,
    'aria-invalid': enErreur ? true : undefined,
    required: requis ? true : undefined,
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={idChamp} className="text-sm font-medium text-slate-800">
        {label}
        {requis && (
          <span className="ml-0.5 text-red-600" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {typeof children === 'function' ? children(champ) : children}

      {enErreur ? (
        <p id={idErreur} className="text-sm text-red-700" role="alert">
          {erreur}
        </p>
      ) : (
        aide !== undefined && (
          <p id={idAide} className="text-sm text-slate-500">
            {aide}
          </p>
        )
      )}
    </div>
  )
}
